/**
 * Utilidades de localización numérica por país.
 * Permite mostrar separadores de miles y decimales según el locale del tenant activo.
 */
import { useAuthStore } from '@/store/authStore'

// ─── Mapeo país → locale Intl ─────────────────────────────────────────────────
const COUNTRY_LOCALE_MAP: Record<string, string> = {
  CL: 'es-CL',   // miles: '.'  decimales: ','
  PE: 'es-PE',   // miles: ','  decimales: '.'
  AR: 'es-AR',   // miles: '.'  decimales: ','
  CO: 'es-CO',   // miles: '.'  decimales: ','
  MX: 'es-MX',   // miles: ','  decimales: '.'
  US: 'en-US',   // miles: ','  decimales: '.'
  EC: 'es-EC',
  BO: 'es-BO',
  PY: 'es-PY',
  UY: 'es-UY',
}

export const DEFAULT_LOCALE = 'es-CL'

/** Retorna el locale Intl del tenant activo (ej: 'es-CL'). */
export function useTenantLocale(): string {
  const country = useAuthStore(s => s.activeTenant?.country)
  return COUNTRY_LOCALE_MAP[country ?? ''] ?? DEFAULT_LOCALE
}

// ─── Rótulo del identificador tributario por país ────────────────────────────
const COUNTRY_TAX_ID_LABEL: Record<string, string> = {
  CL: 'RUT',   // Chile
  PE: 'RUC',   // Perú
  EC: 'RUC',   // Ecuador
  AR: 'CUIT',  // Argentina
  CO: 'NIT',   // Colombia
  MX: 'RFC',   // México
}

export const DEFAULT_TAX_ID_LABEL = 'RUT'

/** Rótulo del identificador tributario del tenant activo (RUT/RUC/CUIT/…). */
export function useTaxIdLabel(): string {
  const country = useAuthStore(s => s.activeTenant?.country)
  return COUNTRY_TAX_ID_LABEL[country ?? ''] ?? DEFAULT_TAX_ID_LABEL
}

// ─── El impuesto al valor agregado, por país ──────────────────────────────────

/**
 * Nombre y tasa del impuesto de cada país.
 *
 * ⚠️ Es un **respaldo, no la fuente de verdad**: el impuesto con el que opera
 * cada tenant se configura en Configuración → Localización
 * (`LocalizationConfig.tax_name` y `.tax_rate`), porque la tasa puede cambiar
 * por norma y hay regímenes que no usan la general. Esta tabla sólo cubre el
 * instante en que esa configuración todavía no cargó.
 *
 * Antes ahí iba un `19` escrito a mano, que en un tenant peruano mostraba una
 * tasa chilena mientras llegaba la respuesta. Es el mismo criterio de
 * `COUNTRY_CURRENCY`.
 */
const COUNTRY_TAX: Record<string, { name: string; rate: number }> = {
  CL: { name: 'IVA', rate: 19 },
  PE: { name: 'IGV', rate: 18 },
  AR: { name: 'IVA', rate: 21 },
  CO: { name: 'IVA', rate: 19 },
  MX: { name: 'IVA', rate: 16 },
  EC: { name: 'IVA', rate: 15 },
  BO: { name: 'IVA', rate: 13 },
  PY: { name: 'IVA', rate: 10 },
  UY: { name: 'IVA', rate: 22 },
}

export const DEFAULT_TAX_NAME = 'IVA'
export const DEFAULT_TAX_RATE = 19

export interface TenantTax {
  /** Cómo se llama el impuesto acá: «IVA» en Chile, «IGV» en Perú. */
  name: string
  /** La tasa en porcentaje (19, no 0,19). */
  rate: number
  /**
   * El país que resolvió este impuesto, en ISO-2 y mayúsculas.
   *
   * Está acá porque algunos textos no dependen del nombre del impuesto sino del
   * régimen: la sigla `IndExe` es del DTE chileno, y nombrarla en un tenant
   * peruano manda a buscar un campo que su comprobante no tiene. Quien necesita
   * eso ya tiene el impuesto a mano, así que exponerlo evita una consulta
   * paralela al país que podría resolver distinto.
   */
  country: string
}

/** Nombre y tasa del impuesto de `country`, o el default si no está en la tabla. */
export function countryTax(country?: string | null): TenantTax {
  // `String(...)` y no `country.toUpperCase()`: un helper de presentación no
  // puede tumbar una pantalla si le llega algo que no es un código de país.
  const codigo = String(country ?? '').toUpperCase()
  const conocido = COUNTRY_TAX[codigo]
  return {
    name: conocido?.name ?? DEFAULT_TAX_NAME,
    rate: conocido?.rate ?? DEFAULT_TAX_RATE,
    // ⚠️ Se conserva el código recibido aunque no esté en la tabla: que no
    // sepamos su impuesto no significa que sea Chile. Vacío es «no se sabe», y
    // los textos condicionados a `=== 'CL'` caen del lado neutro.
    country: codigo,
  }
}

/**
 * Resuelve el impuesto combinando la configuración del tenant con el respaldo
 * de su país.
 *
 * Lo que manda es la configuración; el país sólo llena lo que venga vacío. Se
 * exporta aparte del hook para poder probarla sin montar React y para que la
 * use `tenantTax()`.
 */
export function resolveTax(
  country?: string | null,
  taxName?: string | null,
  taxRate?: string | number | null,
): TenantTax {
  const respaldo = countryTax(country)
  const rate = typeof taxRate === 'number' ? taxRate : parseFloat(String(taxRate ?? ''))
  return {
    name: (taxName ?? '').trim() || respaldo.name,
    rate: isNaN(rate) ? respaldo.rate : rate,
    country: respaldo.country,
  }
}

/** Obtiene el separador decimal del locale (ej: ',' para es-CL). */
export function getDecimalSeparator(locale: string): string {
  return (
    new Intl.NumberFormat(locale).formatToParts(1.1).find(p => p.type === 'decimal')?.value ?? '.'
  )
}

/** Obtiene el separador de miles del locale (ej: '.' para es-CL). */
export function getThousandsSeparator(locale: string): string {
  return (
    new Intl.NumberFormat(locale).formatToParts(1000).find(p => p.type === 'group')?.value ?? ','
  )
}

/** Formatea un número con separador de miles y decimales según el locale. */
export function formatNumber(value: number, locale: string, decimals = 2): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

// ─── Formateo de fechas ──────────────────────────────────────────────────────

const _dateCache = new Map<string, Intl.DateTimeFormat>()
const _dateTimeCache = new Map<string, Intl.DateTimeFormat>()

function _getDateFmt(locale: string): Intl.DateTimeFormat {
  if (!_dateCache.has(locale))
    _dateCache.set(locale, new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }))
  return _dateCache.get(locale)!
}

function _getDateTimeFmt(locale: string): Intl.DateTimeFormat {
  if (!_dateTimeCache.has(locale))
    _dateTimeCache.set(locale, new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }))
  return _dateTimeCache.get(locale)!
}

/**
 * Formatea una fecha ISO (YYYY-MM-DD) según el locale.
 * Ej (es-CL): '2026-03-15' → '15-03-2026'
 * Ej (en-US): '2026-03-15' → '03/15/2026'
 */
export function formatDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—'
  const str = iso.includes('T') ? iso.split('T')[0] : iso
  const [y, m, d] = str.split('-')
  if (!y || !m || !d) return iso
  // Usar Date UTC para evitar timezone shift
  const date = new Date(Date.UTC(parseInt(y), parseInt(m) - 1, parseInt(d)))
  return _getDateFmt(locale).format(date)
}

/**
 * Formatea un datetime ISO según el locale.
 * Ej (es-CL): '2026-03-15T14:30:00Z' → '15-03-2026, 14:30'
 */
export function formatDateTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—'
  try {
    const date = new Date(iso)
    return _getDateTimeFmt(locale).format(date)
  } catch {
    return iso
  }
}

/**
 * Parsea un string formateado con el locale dado a string numérico puro.
 * Ej (es-CL): '1.234.567,89' → '1234567.89'
 * Ej (es-PE): '1,234,567.89' → '1234567.89'
 * Retorna '' si el string está vacío o no es parseable.
 */
export function parseLocalizedNumber(str: string, locale: string): string {
  if (!str.trim()) return ''
  const decSep = getDecimalSeparator(locale)
  const thouSep = getThousandsSeparator(locale)
  const normalized = str
    .split(thouSep).join('')       // quitar separadores de miles
    .replace(decSep, '.')          // normalizar decimal a '.'
    .trim()
  const num = parseFloat(normalized)
  return isNaN(num) ? '' : String(num)
}

// ─── Montos con su moneda ─────────────────────────────────────────────────────

/**
 * Moneda por defecto de cada pais, para cuando el monto no trae la suya.
 *
 * Es un respaldo, no la fuente de verdad: la moneda con la que opera el tenant
 * vive en `LocalizationConfig.currency` y un documento puede estar en otra. Se
 * usa para no tener que pedir la configuracion en cada pantalla que muestra un
 * total.
 */
const COUNTRY_CURRENCY: Record<string, string> = {
  CL: 'CLP',
  PE: 'PEN',
  AR: 'ARS',
  CO: 'COP',
  MX: 'MXN',
  US: 'USD',
  EC: 'USD',   // Ecuador esta dolarizado
  BO: 'BOB',
  PY: 'PYG',
  UY: 'UYU',
}

export const DEFAULT_CURRENCY = 'CLP'

/**
 * Monedas en las que un tenant puede tener una cuenta contable, ademas de su
 * moneda base.
 *
 * ⚠️ La **UF** es chilena (Unidad de Fomento) y no existe en el resto de los
 * paises: ofrecerla en el selector de un tenant peruano es ofrecer algo que su
 * contabilidad no puede usar. Es el mismo patron que el PR #1016 corrigio en los
 * montos, donde el CLP estaba fijo en 30+ lugares.
 */
const FOREIGN_CURRENCIES_BY_COUNTRY: Record<string, Array<[string, string]>> = {
  CL: [
    ['USD', 'USD — Dólar estadounidense'],
    ['UF', 'UF — Unidad de Fomento'],
    ['EUR', 'EUR — Euro'],
    ['JPY', 'JPY — Yen Japonés'],
  ],
  PE: [
    ['USD', 'USD — Dólar estadounidense'],
    ['EUR', 'EUR — Euro'],
  ],
}

const FOREIGN_CURRENCIES_FALLBACK: Array<[string, string]> = [
  ['USD', 'USD — Dólar estadounidense'],
  ['EUR', 'EUR — Euro'],
]

/**
 * Las monedas extranjeras que tiene sentido ofrecer en este tenant.
 *
 * Un pais sin lista propia cae en USD y EUR, que son las dos que cualquiera
 * puede necesitar: es mejor quedarse corto que ofrecer una unidad de otro pais.
 */
export function useForeignCurrencies(): Array<[string, string]> {
  const country = useAuthStore(s => s.activeTenant?.country)
  return FOREIGN_CURRENCIES_BY_COUNTRY[country ?? ''] ?? FOREIGN_CURRENCIES_FALLBACK
}

/**
 * Decimales con los que se expresa cada moneda (exponente ISO 4217).
 *
 * Gemelo de `_CURRENCY_DECIMALS` en `apps/localization/currency.py`: el backend
 * redondea los montos con esta misma tabla, asi que si divergen la pantalla
 * muestra un numero distinto del que se guardo.
 */
const CURRENCY_DECIMALS: Record<string, number> = {
  CLP: 0, JPY: 0, PYG: 0,
  PEN: 2, USD: 2, EUR: 2, UF: 2,
}

/** Cuantos decimales lleva `currency`. Dos es el default y el lado conservador. */
export function currencyDecimals(currency?: string | null): number {
  return CURRENCY_DECIMALS[(currency ?? '').toUpperCase()] ?? 2
}

/**
 * Redondea un monto a los decimales de su moneda.
 *
 * Gemelo de `quantize_money` en `apps/localization/currency.py`. Reemplaza los
 * `Math.round(...)` sueltos, que truncaban a entero cualquier moneda: en soles
 * el formulario mostraba un total sin centimos mientras el backend guardaba el
 * valor con ellos, asi que la pantalla y lo guardado no coincidian.
 */
export function roundMoney(value: number, currency?: string | null): number {
  const factor = Math.pow(10, currencyDecimals(currency))
  return Math.round(value * factor) / factor
}

/**
 * Paises cuyo comprobante electronico puede EMITIRSE en moneda extranjera.
 *
 * 🔴 Chile **no esta**, y no es un olvido: el DTE del SII no tiene donde
 * declarar otra moneda, asi que un documento chileno marcado en dolares
 * produciria un XML que dice pesos con importes que no lo son. Ahi la moneda de
 * origen sobrevive como referencia en la linea y el DTE va en pesos.
 *
 * Espeja `_PAISES_CON_EMISION_EN_MONEDA_EXTRANJERA` del backend, que es quien
 * lo hace cumplir: esto solo decide si se muestra el selector.
 */
const PAISES_CON_EMISION_EN_MONEDA_EXTRANJERA = new Set(['PE'])

/** ¿El tenant activo puede emitir comprobantes en otra moneda? */
export function useEmiteEnMonedaExtranjera(): boolean {
  const country = useAuthStore(s => s.activeTenant?.country)
  return PAISES_CON_EMISION_EN_MONEDA_EXTRANJERA.has(country ?? '')
}

/** Moneda por defecto del tenant activo, derivada de su pais. */
export function useTenantCurrency(): string {
  const country = useAuthStore(s => s.activeTenant?.country)
  return COUNTRY_CURRENCY[country ?? ''] ?? DEFAULT_CURRENCY
}

/**
 * Formatea un monto con el locale y la moneda que corresponden.
 *
 * ⚠️ **No fuerza la cantidad de decimales**, y eso es lo importante: `Intl` ya
 * sabe cuantos lleva cada moneda —CLP ninguno, PEN y USD dos— asi que forzar
 * `maximumFractionDigits: 0`, que es lo que hacia el codigo viejo, le come los
 * centimos a un total en soles. Verificado: con `es-CL` + `CLP` la salida es
 * identica a la anterior (`$1.180`), y con `es-PE` + `PEN` pasa de `S/ 2,360` a
 * `S/ 2,360.00`.
 *
 * Se puede pasar `currency` cuando el monto tiene la suya (una factura en USD,
 * por ejemplo); si no, se usa la del pais del tenant.
 */
export function formatMoney(
  value: number | string | null | undefined,
  locale: string,
  currency: string = DEFAULT_CURRENCY,
): string {
  const num = typeof value === 'number' ? value : parseFloat(String(value ?? ''))
  if (isNaN(num)) return '—'
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(num)
}

/**
 * Hook con el formateador ya atado al tenant activo.
 *
 * `fmt(1180)` usa la moneda del pais; `fmt(500, 'USD')` la que se le indique.
 */
export function useMoney(): (value: number | string | null | undefined, currency?: string) => string {
  const locale = useTenantLocale()
  const moneda = useTenantCurrency()
  return (value, currency) => formatMoney(value, locale, currency || moneda)
}

/**
 * El país del tenant leído del store, para los formateadores que corren fuera
 * de React.
 *
 * ⚠️ Es defensivo a propósito. Un formateador de montos **no puede tumbar una
 * pantalla**: si el store no está inicializado —o está mockeado sin `getState`,
 * que fue lo que rompió un test al introducir esto— devuelve vacío y se cae al
 * formato por defecto. Mostrar el formato equivocado es un problema; no mostrar
 * nada es peor.
 */
function paisDelTenantSeguro(): string {
  try {
    return useAuthStore.getState?.()?.activeTenant?.country ?? ''
  } catch {
    return ''
  }
}

/**
 * Igual que `useMoney`, pero **fuera de React**.
 *
 * Existe porque casi todos los formateadores de este código son `const fmt =
 * (v) => …` a nivel de módulo, donde no se puede llamar a un hook. Convertirlos
 * a hooks obligaría a mover cada definición dentro de su componente y a tocar
 * todas sus llamadas: mucho más riesgo por el mismo resultado.
 *
 * Lee el tenant del store en cada llamada. No es reactivo, y no hace falta que
 * lo sea: cambiar de empresa navega a otro subdominio y recarga la aplicación.
 */
export function formatMoneyForTenant(
  value: number | string | null | undefined,
  currency?: string,
): string {
  const country = paisDelTenantSeguro()
  const locale = COUNTRY_LOCALE_MAP[country] ?? DEFAULT_LOCALE
  return formatMoney(value, locale, currency || COUNTRY_CURRENCY[country] || DEFAULT_CURRENCY)
}

/**
 * El locale del tenant activo, fuera de React.
 *
 * Para los formateadores que ya reciben su propia moneda (una factura en USD,
 * un tipo de cambio) y solo necesitan los separadores correctos.
 */
export function tenantLocale(): string {
  return COUNTRY_LOCALE_MAP[paisDelTenantSeguro()] ?? DEFAULT_LOCALE
}

/**
 * El impuesto del tenant activo derivado sólo de su país, **fuera de React**.
 *
 * Para los rótulos que viven en formateadores a nivel de módulo, donde no se
 * puede llamar a un hook. No ve la configuración del tenant, así que da la tasa
 * general del país: si el tenant la tiene distinta, usar `useTenantTax`.
 */
export function tenantTax(): TenantTax {
  return countryTax(paisDelTenantSeguro())
}

/** Moneda por defecto del tenant activo, fuera de React. */
export function tenantCurrency(): string {
  return COUNTRY_CURRENCY[paisDelTenantSeguro()] ?? DEFAULT_CURRENCY
}

/** Separadores del tenant sin símbolo de moneda, fuera de React. */
export function formatNumberForTenant(
  value: number | string | null | undefined,
  decimals = 0,
): string {
  const country = paisDelTenantSeguro()
  const num = typeof value === 'number' ? value : parseFloat(String(value ?? ''))
  if (isNaN(num)) return '—'
  return formatNumber(num, COUNTRY_LOCALE_MAP[country] ?? DEFAULT_LOCALE, decimals)
}
